// Business year (`/business`, design `#s08`). Profile remains the live
// `/api/business-profile` write. Year totals wait on a year endpoint; the
// chart hatches unknown months instead of drawing $0.
import { useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button, Skeleton } from '@moneyskyline/ui';
import { HonestInline } from '@/components/HonestInline';
import { AmountText } from '@/components/AmountText';
import { useShellContext } from '@/app/shell/AppShell';
import { scopeHref } from '@/app/shell/scope';
import { useBusinessProfile, useSaveBusinessProfile, type BusinessProfileRow } from '@/api/hooks/useBusinessProfile';
import { useBusinessYear, type BusinessYearResult } from '@/api/hooks/useBusinessYear';
import { useMe } from '@/api/hooks/useMe';
import { useRules } from '@/api/hooks/useRules';

function calendarYearInTimeZone(timezone: string, instant = new Date()): number {
  const year = new Intl.DateTimeFormat('en-US', { timeZone: timezone, year: 'numeric' }).formatToParts(instant).find((part) => part.type === 'year')?.value;
  return Number(year);
}

function seriesEmptyCopy(year: BusinessYearResult | undefined, kind: 'expense' | 'income'): string {
  const settledEmpty =
    year?.status === 'empty' ||
    (year?.status === 'ready' && (kind === 'expense' ? year.expenses.length === 0 : year.income_sources.length === 0));
  if (settledEmpty) {
    return kind === 'expense'
      ? 'No business-tagged expenses this period.'
      : 'No business-tagged income deposits this period.';
  }
  return kind === 'expense' ? 'Expense breakdown isn’t available yet.' : 'Income sources aren’t available yet.';
}
import { BusinessNav } from './BusinessNav';
import { YearChart } from './YearChart';
import { RecordsDisclaimer } from './RecordsDisclaimer';
import { AccountantWorkPanel } from './AccountantWorkPanel';
import { PromotionCandidatesPage } from './PromotionCandidatesPage';

function BusinessProfileForm({
  initialProfile,
  justSaved,
  onSaved,
  onSaving,
}: {
  initialProfile: BusinessProfileRow | null;
  justSaved: boolean;
  onSaved: () => void;
  onSaving: () => void;
}) {
  const saveProfile = useSaveBusinessProfile();
  const [businessType, setBusinessType] = useState(initialProfile?.business_type ?? '');
  const [description, setDescription] = useState(initialProfile?.description ?? '');

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    onSaving();
    saveProfile.mutate(
      { business_type: businessType.trim() || null, description: description.trim() || null },
      { onSuccess: onSaved },
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-3" data-testid="business-profile-form">
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-xs text-muted-foreground">Entity type</span>
        <input
          value={businessType}
          onChange={(event) => setBusinessType(event.target.value)}
          placeholder="e.g. sole proprietor, LLC"
          className="min-h-11 rounded-md border border-input px-3 text-sm"
        />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-xs text-muted-foreground">Description</span>
        <textarea
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="A short description a rule-reading model can use as context."
          rows={4}
          className="rounded-md border border-input px-3 py-2 text-sm"
        />
      </label>
      {saveProfile.isError && <HonestInline kind="api-unavailable" />}
      {justSaved && !saveProfile.isError && (
        <p role="status" data-testid="business-profile-saved">
          Saved
        </p>
      )}
      <div>
        <Button type="submit" disabled={saveProfile.isPending}>
          {saveProfile.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  );
}

export function BusinessPage() {
  const { band } = useShellContext();
  const { search } = useLocation();
  const meQuery = useMe();
  const taxYear = meQuery.data?.timezone ? calendarYearInTimeZone(meQuery.data.timezone) : undefined;
  const { data: profile, isLoading, isError } = useBusinessProfile();
  const yearQuery = useBusinessYear(taxYear, meQuery.data?.timezone);
  const rulesQuery = useRules();
  const [justSaved, setJustSaved] = useState(false);
  const year = yearQuery.data;
  const rules = rulesQuery.data ?? [];
  const desktop = band === 'desktop';
  const displayYear = taxYear ?? (year && year.status !== 'unavailable' ? year.tax_year : undefined);

  return (
    <section className="mx-auto w-full max-w-6xl px-4 py-6" aria-labelledby="business-heading">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 id="business-heading" tabIndex={-1} className="text-title font-semibold">
            Business
          </h1>
          <p className="text-sm text-muted-foreground">
            {year?.status === 'ready' || year?.status === 'empty' || year?.status === 'insufficient'
              ? `Business ${year.tax_year}`
              : `Business ${displayYear ?? ''}`}
            {profile?.business_type ? ` · ${profile.business_type}` : null}
            {year?.status === 'ready' ? ` · ${year.period_label}` : null}
          </p>
        </div>
        <Link
          to={scopeHref('/business/export', search)}
          className="inline-flex min-h-11 items-center rounded-md bg-foreground px-4 text-sm font-semibold text-background"
        >
          Review &amp; export
        </Link>
      </div>

      <BusinessNav
        conditionCount={rulesQuery.data ? rules.length : null}
        eventCount={null}
        eventHref={null}
      />

      {displayYear != null && <AccountantWorkPanel taxYear={displayYear} />}
      {displayYear != null && <PromotionCandidatesPage taxYear={displayYear} />}

      {isLoading && (
        <div className="mt-4 flex flex-col gap-2">
          <Skeleton className="h-11 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      )}

      {!isLoading && isError && <HonestInline className="mt-4" kind="api-unavailable" />}

      <div className={desktop ? 'mt-4 grid grid-cols-[minmax(0,1fr)_400px] gap-6' : 'mt-4 flex flex-col gap-6'}>
        <div>
          <div className="rounded-[10px] border border-border bg-card p-4" data-testid="business-year-summary">
            {yearQuery.isLoading && <Skeleton className="h-24 w-full" />}
            {yearQuery.isError && <HonestInline kind="api-unavailable" />}
            {year?.status === 'unavailable' && !yearQuery.isError && (
              <div data-testid="business-year-unavailable">
                <HonestInline kind="business-unavailable" />
                <YearChart unavailable />
              </div>
            )}
            {year?.status === 'empty' && (
              <p role="status" className="text-sm text-muted-foreground">
                No business-tagged items in {year.period_label}. This is not a $0 year.
              </p>
            )}
            {year?.status === 'insufficient' && (
              <p role="status" className="text-sm text-muted-foreground" data-testid="business-year-insufficient">
                {year.reason === 'partial_source'
                  ? 'Some accounts need attention. This year is not a complete rollup.'
                  : 'Business totals need a single currency. This year is not summed.'}
              </p>
            )}
            {year?.status === 'ready' && (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <div className="text-[12px] text-muted-foreground">Income</div>
                    <AmountText value={year.income} currency={year.currency} className="text-lg font-semibold" />
                  </div>
                  <div>
                    <div className="text-[12px] text-muted-foreground">Expenses</div>
                    <AmountText value={year.expense} currency={year.currency} className="text-lg font-semibold" />
                  </div>
                  <div>
                    <div className="text-[12px] text-muted-foreground">Net (income − expenses)</div>
                    <AmountText value={year.net} currency={year.currency} variant="balance" className="text-lg font-semibold" />
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-1 gap-2 border-t border-border pt-3 text-[12px] text-muted-foreground tablet:grid-cols-3">
                  <div>Year to date <AmountText value={year.net} currency={year.currency} variant="balance" className="text-foreground" /></div>
                  <div>
                    Prior year
                    {year.prior_year_net ? (
                      <AmountText value={year.prior_year_net} currency={year.currency} variant="balance" className="ms-degraded-underline text-foreground" />
                    ) : (
                      <span className="ml-1">unknown</span>
                    )}
                  </div>
                  <div>
                    Average per month
                    {year.average_per_month ? (
                      <>
                        {' '}
                        <AmountText value={year.average_per_month} currency={year.currency} variant="balance" className="text-foreground" />
                        {year.average_month_count != null ? ` · ${year.average_month_count} months` : null}
                      </>
                    ) : (
                      <span className="ml-1">unknown</span>
                    )}
                  </div>
                </div>
                <YearChart months={year.months} currency={year.currency} />
                <p className="mt-2 text-[12px] text-muted-foreground">
                  Transfers and card payments are excluded from both series. Future months are hatched, never zero.
                </p>
              </>
            )}
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 desktop:grid-cols-2">
            <div className="rounded-[10px] border border-border bg-card text-sm">
              <div className="px-3 pt-2.5 text-[12px] text-muted-foreground">Expenses by category · records, not deductions</div>
              {year?.status === 'ready' && year.expenses.length > 0 ? (
                year.expenses.map((row) => (
                  <Link
                    key={row.label}
                    to={scopeHref('/transactions/categories', search)}
                    className="flex min-h-11 items-center justify-between border-t border-border px-3 text-foreground first:border-t-0"
                  >
                    <span>
                      {row.label}
                      {row.item_count != null ? <span className="text-[12px] text-muted-foreground"> · {row.item_count} items</span> : null}
                      {row.uncategorized_count ? <span className="text-[12px] text-money-negative"> · {row.uncategorized_count} uncategorized</span> : null}
                      {row.note ? <span className="text-[12px] text-muted-foreground"> · {row.note}</span> : null}
                    </span>
                    <AmountText value={row.amount} currency={year.currency} />
                  </Link>
                ))
              ) : (
                <p className="px-3 py-3 text-sm text-muted-foreground" role="status">
                  {seriesEmptyCopy(year, 'expense')}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-4">
              <div className="rounded-[10px] border border-border bg-card text-sm">
                <div className="px-3 pt-2.5 text-[12px] text-muted-foreground">Income sources · deposits tagged income</div>
                {year?.status === 'ready' && year.income_sources.length > 0 ? (
                  year.income_sources.map((row) => (
                    <div key={row.label} className="flex min-h-11 items-center justify-between px-3">
                      <span>
                        {row.label}
                        <span className="text-[12px] text-muted-foreground">
                          {row.deposit_count != null ? ` · ${row.deposit_count} deposits` : ''}
                          {row.account_last4 ? ` · •${row.account_last4}` : ''}
                        </span>
                      </span>
                      <AmountText value={row.amount} currency={year.currency} />
                    </div>
                  ))
                ) : (
                  <p className="px-3 py-3 text-sm text-muted-foreground" role="status">
                    {seriesEmptyCopy(year, 'income')}
                  </p>
                )}
              </div>
              <div className="rounded-[10px] border border-border bg-card text-sm">
                <div className="px-3 pt-2.5 text-[12px] text-muted-foreground">Events</div>
                <p className="px-3 py-3 text-sm text-muted-foreground" role="status">
                  Saved events aren’t available to view yet.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold">Conditions <span className="font-normal text-muted-foreground">· why things are business</span></h2>
            <Link to={scopeHref('/business/conditions', search)} className="text-sm text-link">Edit · + New</Link>
          </div>
          {rulesQuery.isError && <HonestInline kind="api-unavailable" />}
          {rulesQuery.data && rules.length === 0 && (
            <p role="status" className="rounded-[10px] border border-border bg-card p-3 text-sm text-muted-foreground">
              No conditions yet. New matching charges stay personal until you add one.
            </p>
          )}
          {rules.length > 0 && (
            <div className="flex flex-col gap-2">
              {rules.map((rule) => (
                <Link
                  key={rule.rule_id}
                  to={scopeHref(`/business/conditions?rule=${rule.rule_id}`, search)}
                  className="rounded-[10px] border border-border bg-card p-3 text-sm text-foreground"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{rule.kind}</span>
                    <span className={rule.enabled ? 'h-5 w-[34px] rounded-full bg-money-positive' : 'h-5 w-[34px] rounded-full bg-input'} aria-hidden />
                  </div>
                  <div className="mt-1 font-semibold">{rule.restatement}{rule.enabled ? '' : ' · off'}</div>
                  <div className="mt-0.5 text-[12px] text-muted-foreground">
                    {rule.effective_from ?? '—'} – {rule.effective_to ?? '—'} · {rule.match_count} matching
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {!isLoading && !isError && (
        <div className="mt-6 max-w-xl">
          <h2 className="text-sm font-semibold">Business profile</h2>
          <BusinessProfileForm
            key={profile ? 'loaded' : 'empty'}
            initialProfile={profile ?? null}
            justSaved={justSaved}
            onSaved={() => setJustSaved(true)}
            onSaving={() => setJustSaved(false)}
          />
        </div>
      )}

      <RecordsDisclaimer className="mt-6 max-w-xl" />
    </section>
  );
}

export default BusinessPage;
